/* ══ v18: THE SERVER — one Node process, one SQLite database, a login per person ══════════════
   Everything the app used to do with part files, folder grants and the launcher's /__ endpoints
   now goes through the JSON API under /api/. This file is the whole client side of that:
     · _api()                 the one fetch wrapper (network / 401 / error classification)
     · login, logout, change password, user admin
     · _srvFetchPortfolio()   GET /api/portfolio + /api/prefs → a DB object for _adoptDB
     · _srvPlanToChanges()    the rev engine's plan → record-level changes for POST /api/save
     · conflicts (409)        what the user sees when a colleague saved the same record first
     · the change feed        colleagues' saves picked up on focus and every 60 s

   ⚠ POSITION IN THE BUNDLE IS LOAD-BEARING. The `let`s below are read by banner and Save-button
   code that runs during the boot-time renders far further down the bundle, so they must be
   initialised before any of it executes — hence this file sits directly after shared/version.js.
   Everything else here is a hoisted function declaration and is safe anywhere. */

let _srvUser = null;            /* {username, displayName, role} once logged in */
let _srvHasUsers = true;
let _srvChangeSeq = 0;          /* the change-feed position this tab's portfolio reflects */
const _srvOwnSeqs = new Set();  /* feed entries this tab produced itself */
let _srvReach = 'ok';           /* 'ok' | 'unavailable' */
let _srvColleagues = [];        /* [{writer, at}] saves by others this (dirty) tab has not adopted */
let _srvRefreshError = null;    /* why the last background refresh kept the old picture */
let _srvLoadError = null;       /* why the portfolio could not be loaded at boot */
let _srvPrefs = {};             /* {scope: {key: value}} — per-user, never shared data */
let _srvConflict = null;        /* the refused save, while its dialog is open */
let _srvPollTimer = null;
let _srvCheckFlight = null;
let _srvAuthLost = false;
let _srvLoginWaiters = [];
let _srvUsersCache = null;      /* admin-only roster, for display names */

const SRV_POLL_MS = 60000;
const SRV_LOGIN_ERROR = 'That username and password do not match.';

/* ── the fetch wrapper ──────────────────────────────────────────────────────────────────── */
async function _api(method, path, body) {
  let r;
  try {
    r = await fetch(path, {
      method: method, cache: 'no-store', credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    _srvSetReach('unavailable');
    const err = new Error('The FlightDeck server could not be reached.');
    err.network = true;
    throw err;
  }
  _srvSetReach('ok');
  let j = null;
  try { j = await r.json(); } catch (e) { /* an empty or non-JSON body is reported by status */ }
  if (!r.ok) {
    if (r.status === 401 && path !== '/api/login') _srvOnAuthLost();
    const err = new Error((j && j.message) || ('The server answered ' + r.status + '.'));
    err.status = r.status; err.code = j && j.error; err.body = j;
    throw err;
  }
  return j;
}
function _srvSetReach(state) {
  if (_srvReach === state) return;
  _srvReach = state;
  const live = document.getElementById('launcherAnnouncement');
  if (live) live.textContent = state === 'unavailable' ? SRV_UNREACHABLE_TEXT : '';
  if (_bootDone) _repaintFileState();
}
const SRV_UNREACHABLE_TEXT = 'The FlightDeck server cannot be reached. Your unsaved changes are still here; Save again once it is back.';

/* ── preferences (per user; replaces IndexedDB lastProgramme/lastView and the per-machine
   appMeta UI keys that used to ride inside every writer's part file) ───────────────────── */
function _srvPref(scope, key) {
  const s = _srvPrefs[scope];
  return (s && Object.prototype.hasOwnProperty.call(s, key)) ? s[key] : undefined;
}
function _srvSetPref(scope, key, value) {
  if (!_srvUser) return Promise.resolve();
  if (!_srvPrefs[scope]) _srvPrefs[scope] = {};
  if (value === undefined || value === null) delete _srvPrefs[scope][key];
  else _srvPrefs[scope][key] = value;
  return _api('PUT', '/api/prefs', { scope: scope, key: key, value: value === undefined ? null : value })
    .catch(e => console.warn('A personal preference could not be saved:', e));
}
/* REV_LOCAL_APPMETA keys are the project's per-person view state (sort, collapsed sections,
   meeting timer…). They never bump a rev, so they are not shared data: they go to prefs. */
function _srvLocalAppMetaOf(p) {
  const out = {};
  if (p && p.appMeta && typeof p.appMeta === 'object')
    REV_LOCAL_APPMETA.forEach(k => {
      if (p.appMeta[k] !== undefined && SHARED_UNVERSIONED_APPMETA.indexOf(k) === -1) out[k] = p.appMeta[k];
    });
  return out;
}
/* SHARED_UNVERSIONED_APPMETA (the meeting baseline) is team state: it stays in the project on the
   server and is written through its own unversioned endpoint after a successful save. `_srvSharedSeen`
   is what the server last held, so only a real change is sent. */
const _srvSharedSeen = new Map();   /* 'uid|key' -> canonical JSON */
function _srvNoteShared(db) {
  _srvSharedSeen.clear();
  ((db && db.projects) || []).forEach(p => {
    if (!p || p.del || !p.uid) return;
    SHARED_UNVERSIONED_APPMETA.forEach(k => {
      _srvSharedSeen.set(p.uid + '|' + k, _revStable(p.appMeta && p.appMeta[k] !== undefined ? p.appMeta[k] : null));
    });
  });
}
function _srvPushShared(db) {
  ((db && db.projects) || []).forEach(p => {
    if (!p || p.del || !p.uid) return;
    SHARED_UNVERSIONED_APPMETA.forEach(k => {
      const v = p.appMeta && p.appMeta[k] !== undefined ? p.appMeta[k] : null;
      const canon = _revStable(v), id = p.uid + '|' + k;
      if (_srvSharedSeen.get(id) === canon) return;
      _srvSharedSeen.set(id, canon);
      _api('PUT', '/api/projects/' + encodeURIComponent(p.uid) + '/shared', { key: k, value: v })
        .catch(e => { _srvSharedSeen.delete(id); console.warn('The meeting baseline could not be saved:', e); });
    });
  });
}
function _srvPushLocalAppMeta(db) {
  ((db && db.projects) || []).forEach(p => {
    if (!p || p.del || !p.uid) return;
    const loc = _srvLocalAppMetaOf(p);
    const prev = _srvPref('project:' + p.uid, 'appMeta');
    if (_revStable(prev === undefined ? {} : prev) === _revStable(loc)) return;
    _srvSetPref('project:' + p.uid, 'appMeta', loc);
  });
}

/* ── loading ────────────────────────────────────────────────────────────────────────────── */
async function _srvFetchPortfolio() {
  const both = await Promise.all([_api('GET', '/api/portfolio'), _api('GET', '/api/prefs')]);
  const p = both[0] || {}, prefs = both[1] || {};
  _srvPrefs = (prefs.prefs && typeof prefs.prefs === 'object') ? prefs.prefs : {};
  const db = {
    schemaVersion: p.schemaVersion,
    savedAt: (typeof p.savedAt === 'string' && p.savedAt) ? p.savedAt : undefined,
    programmes: Array.isArray(p.programmes) ? p.programmes : [],
    projects: Array.isArray(p.projects) ? p.projects : [],
  };
  db.projects.forEach(pr => {
    const loc = pr && pr.uid ? _srvPref('project:' + pr.uid, 'appMeta') : undefined;
    if (loc && typeof loc === 'object') {
      const own = {};
      Object.keys(loc).forEach(k => { if (SHARED_UNVERSIONED_APPMETA.indexOf(k) === -1) own[k] = loc[k]; });
      pr.appMeta = Object.assign(pr.appMeta || {}, own);
    }
  });
  _srvNoteShared(db);
  /* The server sends deletion markers beside the live records rather than inside them. Fold
     them back in so _adoptDB harvests them through _revAdoptBaseline exactly as it harvested a
     merged portfolio's: id minting and display numbering read retired ids from `_revTombs`. */
  if (Array.isArray(p.tombstones) && p.tombstones.length) _revFoldTombstones(db, p.tombstones);
  return { db: db, changeSeq: p.changeSeq | 0 };
}

/* ── saving ─────────────────────────────────────────────────────────────────────────────────
   The rev engine already knows exactly which records changed since the baseline (`_revPlan`):
   each stamp is one changed record, each tomb one deletion. That is precisely what the server
   checks optimistically, so a save is the plan, addressed by the same path keys. A parent record
   is sent WITHOUT its child collections — those are records of their own. */
function _srvPlanToChanges(saveDB, plan) {
  const changes = [];
  _revWalk(saveDB, function (key, rec, spec, kids) {
    const s = plan.stamps.get(key);
    if (!s || !rec || typeof rec !== 'object') return;
    const r = JSON.parse(JSON.stringify(rec));
    (kids ? (MERGE_SPEC[kids] || []) : []).forEach(k => _revDeletePath(r, k.path));
    delete r.rev; delete r.writer; delete r.del;
    if (kids === 'project' && r.appMeta && typeof r.appMeta === 'object')
      REV_LOCAL_APPMETA.forEach(k => { delete r.appMeta[k]; });
    changes.push({ key: key, baseRev: Math.max(0, (s.rev | 0) - 1), record: r });
  });
  plan.tombs.forEach(t => changes.push({ key: t.key, baseRev: Math.max(0, (t.rev | 0) - 1), del: true }));
  return changes;
}
/* Resolves {ok:true, revs, savedAt, changeSeq} or {ok:false, conflicts | auth | network | reason}.
   Never rejects: the caller's state machine branches on the result, exactly as it did on the
   old _writeDataFile result. */
async function _srvPostSave(changes, logEntry) {
  if (!changes.length) return { ok: true, revs: {}, savedAt: null, changeSeq: _srvChangeSeq, empty: true };
  try {
    const res = await _api('POST', '/api/save', {
      changes: changes,
      log: (logEntry && Array.isArray(logEntry.changes) && logEntry.changes.length) ? { changes: logEntry.changes } : null,
    });
    if (res && res.changeSeq) _srvOwnSeqs.add(res.changeSeq | 0);
    return Object.assign({ ok: true }, res);
  } catch (e) {
    if (e.network) return { ok: false, network: true, reason: e.message };
    if (e.status === 401) return { ok: false, auth: true, reason: 'Your session has ended. Sign in again, then press Save — your changes are still here.' };
    if (e.status === 409 && e.body && Array.isArray(e.body.conflicts)) return { ok: false, conflicts: e.body.conflicts };
    return { ok: false, reason: e.message };
  }
}
/* After my own save: whatever else landed in the feed between my last look and my save belongs to
   colleagues, so ask for it now rather than waiting for the next focus. */
function _srvAfterOwnSave(res) {
  if (!res || !res.changeSeq) return;
  if ((res.changeSeq | 0) === _srvChangeSeq + 1) { _srvChangeSeq = res.changeSeq | 0; return; }
  _srvCheckChanges(true);
}

/* ── conflicts ──────────────────────────────────────────────────────────────────────────────
   A 409 means nothing at all was written. The dialog names what collided and who changed it,
   and offers the one resolution that loses nothing silently: save everything ELSE, then take the
   colleague's version of exactly the conflicting records (the portfolio is reloaded from the
   server, which then holds my other edits and their version of those records). Cancel keeps the
   tab exactly as it is, so the user can copy their text out first. */
function _srvConflictLabel(c) {
  const seg = String(c.key || '').split('/');
  const db = window.__DB__ || {};
  const k = _logKindOf(c.key);
  const kind = (LOG_KIND_LABEL[k] || k).replace(/^./, ch => ch.toUpperCase());
  const root = (db[seg[0]] || []).find(r => r && String(r[seg[0] === 'projects' ? 'uid' : 'id']) === seg[1]);
  let what = root ? (root.name || seg[1]) : seg[1];
  if (seg.length > 2) {
    const rec = c.record || null;
    const title = rec ? _logTitleOf(rec) : '';
    what += ' · ' + (title ? '“' + title + '”' : '#' + seg[seg.length - 1]);
  }
  return { kind: kind, what: what };
}
function _srvShowConflict(state) {
  _srvConflict = state;
  const ov = document.getElementById('conflictOverlay');
  const list = document.getElementById('conflictList');
  if (!ov || !list) { alert('Save refused: a colleague changed the same record first. Reload to see their version.'); return; }
  list.innerHTML = state.conflicts.map(c => {
    const l = _srvConflictLabel(c);
    const who = c.writer ? _escapeHtml(_srvDisplayName(c.writer)) : 'someone';
    const verb = c.del ? 'deleted it' : 'changed it';
    return `<li><span class="cf-kind">${_escapeHtml(String(l.kind))}</span> <span class="cf-what">${_escapeHtml(String(l.what))}</span>`
      + ` <span class="cf-who">— ${who} ${verb}</span></li>`;
  }).join('');
  const others = state.changes.length - state.conflicts.length;
  document.getElementById('conflictOthers').textContent = others > 0
    ? `Your other ${others} change${others === 1 ? '' : 's'} will be saved.`
    : 'You have no other changes to save.';
  ov.classList.add('open');
}
function closeConflict() {
  const ov = document.getElementById('conflictOverlay');
  if (ov) ov.classList.remove('open');
  _srvConflict = null;
}
function handleConflictOverlayClick(e) { if (e.target === e.currentTarget) closeConflict(); }
async function srvTakeTheirs() {
  const st = _srvConflict;
  if (!st) return;
  const btn = document.getElementById('conflictTakeBtn');
  if (btn) btn.disabled = true;
  try {
    const bad = new Set(st.conflicts.map(c => c.key));
    const deadParents = st.conflicts.filter(c => c.del).map(c => c.key + '/');
    const keep = k => !bad.has(k) && !deadParents.some(p => k.indexOf(p) === 0);
    const plan = { stamps: new Map(), tombs: st.plan.tombs.filter(t => keep(t.key)) };
    st.plan.stamps.forEach((v, k) => { if (keep(k)) plan.stamps.set(k, v); });
    const changes = _srvPlanToChanges(st.saveDB, plan);
    let logEntry = null;
    try { logEntry = _mintLogEntry(st.saveDB, st.baseline, plan, null); } catch (e) { console.warn('The activity log could not be minted for this save.', e); }
    const res = await _srvPostSave(changes, logEntry);
    if (!res.ok) {
      closeConflict();
      if (res.conflicts) { _srvShowConflict(Object.assign({}, st, { conflicts: res.conflicts, changes: changes, plan: plan })); return; }
      alert('Save FAILED — your changes are NOT saved.\n\n' + (res.reason || '') + '\n\nYour work is still open in this tab.');
      return;
    }
    _srvPushLocalAppMeta(st.saveDB);
    _srvPushShared(st.saveDB);
    closeConflict();
    await _srvRefreshNow({ discardWork: true, reason: 'conflict' });
    showToast(changes.length ? 'Your other changes were saved; the conflicting records now show your colleague’s version.'
                             : 'Loaded your colleague’s version of the conflicting records.', { type: 'warn', duration: 8000 });
  } finally {
    if (btn) btn.disabled = false;
  }
}

/* ── the change feed: colleagues' saves ─────────────────────────────────────────────────────
   A CLEAN tab simply reloads the portfolio and repaints (the v14 auto-refresh-when-clean rule,
   unchanged). A DIRTY tab is told who saved and left alone — adopting under unsaved work would
   discard it. */
function _srvStartPolling() {
  if (_srvPollTimer) return;
  _srvPollTimer = setInterval(function () { if (!document.hidden) _srvCheckChanges(false); }, SRV_POLL_MS);
  const recheck = () => { if (document.visibilityState !== 'hidden') _srvCheckChanges(false); };
  window.addEventListener('focus', recheck);
  document.addEventListener('visibilitychange', recheck);
}
function _srvCheckChanges(force) {
  if (!_bootDone || !_srvUser || _srvAuthLost) return Promise.resolve();
  if (_srvCheckFlight) return _srvCheckFlight;
  _srvCheckFlight = (async function () {
    let feed;
    try { feed = await _api('GET', '/api/changes?since=' + _srvChangeSeq); }
    catch (e) { return; }   /* _api already painted unreachable / logged-out state */
    const theirs = (feed.changes || []).filter(c => !_srvOwnSeqs.has(c.seq | 0));
    if (!theirs.length && !force) { _srvChangeSeq = Math.max(_srvChangeSeq, feed.changeSeq | 0); return; }
    if (!theirs.length && (feed.changeSeq | 0) <= _srvChangeSeq) return;
    if (!_dbLoadOk || _saveNeedsReload || _saveInFlight || _workInProgressBlocksAdoption()) {
      theirs.forEach(c => { if (!_srvColleagues.some(x => x.seq === c.seq)) _srvColleagues.push({ seq: c.seq, writer: c.writer, at: c.at }); });
      _repaintFileState();
      return;
    }
    await _srvRefreshNow({ discardWork: false, reason: 'feed' });
  })().finally(function () { _srvCheckFlight = null; });
  return _srvCheckFlight;
}
/* Fetch and adopt the server's portfolio. `discardWork` is only ever true when the user has
   explicitly chosen it (conflict resolution, the reload button). */
async function _srvRefreshNow(opts) {
  const startingDB = window.__DB__, startingEdit = _editSeq;
  let got;
  try { got = await _srvFetchPortfolio(); }
  catch (e) {
    if (!e.network && e.status !== 401) { _srvRefreshError = e.message; _repaintFileState(); }
    return false;
  }
  if (!opts.discardWork && (_workInProgressBlocksAdoption() || _saveInFlight
      || window.__DB__ !== startingDB || _editSeq !== startingEdit)) {
    _repaintFileState();
    return false;
  }
  try {
    _adoptDB(got.db);
    _srvChangeSeq = got.changeSeq;
    _srvColleagues = [];
    _srvRefreshError = null;
  } catch (e) {
    _srvRefreshError = (e && e.message) || String(e);
    console.warn('Could not adopt the server portfolio, so the previous one is still shown:', e);
  }
  _repaintFileState();
  return true;
}
function srvReloadDiscarding() {
  if (_workInProgressBlocksAdoption()
      && !confirm('Reload from the server and DISCARD your unsaved changes in this tab?')) return;
  _srvRefreshNow({ discardWork: true, reason: 'user' });
}

/* ── banners (composed by _dataFileBannersHtml / _fileAlertStripHtml) ──────────────────────── */
function _srvDisplayName(username) {
  if (_srvUser && username === _srvUser.username) return 'you (in another tab)';
  const u = (_srvUsersCache || []).find(x => x.username === username);
  return (u && u.displayName) || username;
}
function _srvUnreachableBannerHtml() {
  if (_srvReach !== 'unavailable') return '';
  return `<div class="ov-quiet-note launcher-note"><span>${_escapeHtml(SRV_UNREACHABLE_TEXT)}</span>`
    + `<button type="button" class="ov-btn launcher-check" onclick="_srvCheckChanges(true)">Check again</button></div>`;
}
function _srvColleaguesBannerHtml() {
  if (!_srvColleagues.length) return '';
  const names = [...new Set(_srvColleagues.map(c => _srvDisplayName(c.writer)))];
  const who = names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  return `<div class="ov-reconnect ov-stale-view ov-parts-stale"><span class="ov-reconnect-msg">`
    + `<strong>${_escapeHtml(who)} saved since you loaded this.</strong> `
    + `You have unsaved changes, so nothing has been refreshed. Save to publish your work — if you `
    + `both changed the same record you will be asked which version to keep — or reload to discard `
    + `your changes and see theirs.</span>`
    + `<button class="ov-btn" onclick="srvReloadDiscarding()">Reload</button></div>`;
}
function _srvRefreshNoteHtml() {
  if (!_srvRefreshError) return '';
  return `<div class="ov-quiet-note parts-refresh-note"><span>The last refresh could not be applied `
    + `(${_escapeHtml(String(_srvRefreshError))}) — showing the portfolio as it was.</span></div>`;
}
function _srvLoadFailedBannerHtml() {
  if (_dbLoadOk) return '';
  return `<div class="ov-reconnect"><span class="ov-reconnect-msg"><strong>The portfolio could not be loaded from the server.</strong> `
    + `Save is blocked so nothing can overwrite it. ${_escapeHtml(String(_srvLoadError || ''))}</span>`
    + `<button class="ov-btn ov-btn-primary" onclick="location.reload()">Retry</button></div>`;
}

/* ── login, logout, account ───────────────────────────────────────────────────────────────── */
function _srvShowLogin(mode) {
  const ov = document.getElementById('loginOverlay');
  if (!ov) return;
  const msg = document.getElementById('loginMsg');
  const sub = document.getElementById('loginSub');
  if (msg) msg.textContent = '';
  if (sub) {
    sub.textContent = mode === 'expired'
      ? 'Your session has ended. Sign in again — your unsaved changes are still in this tab.'
      : (!_srvHasUsers
        ? 'No accounts exist yet. Ask the person running the server to create one with: npm run create-user -- <name> --admin'
        : 'Sign in to open the portfolio.');
  }
  document.body.classList.add('fd-login-open');
  ov.classList.add('open');
  const u = document.getElementById('loginUser');
  if (u) { if (mode === 'expired' && _srvUser) u.value = _srvUser.username; setTimeout(() => (u.value ? document.getElementById('loginPass') : u).focus(), 0); }
}
function _srvHideLogin() {
  const ov = document.getElementById('loginOverlay');
  if (ov) ov.classList.remove('open');
  document.body.classList.remove('fd-login-open');
}
function _srvOnAuthLost() {
  if (_srvAuthLost) return;
  _srvAuthLost = true;
  _srvShowLogin('expired');
}
/* Boot awaits this when there is no session; an expired session resolves it in place. */
function _srvWaitForLogin() {
  return new Promise(resolve => { _srvLoginWaiters.push(resolve); _srvShowLogin('initial'); });
}
async function submitLogin(ev) {
  if (ev) ev.preventDefault();
  const user = (document.getElementById('loginUser').value || '').trim().toLowerCase();
  const pass = document.getElementById('loginPass').value || '';
  const msg = document.getElementById('loginMsg');
  const btn = document.getElementById('loginBtn');
  if (!user || !pass) { msg.textContent = 'Enter your username and password.'; return false; }
  btn.disabled = true; msg.textContent = '';
  try {
    const res = await _api('POST', '/api/login', { username: user, password: pass });
    const prev = _srvUser;
    document.getElementById('loginPass').value = '';
    if (prev && prev.username !== res.user.username) {
      /* A different person on this tab: everything minted and pending here belongs to the
         previous writer, so start clean rather than publishing their edits under a new name. */
      _srvUser = res.user;
      location.reload();
      return false;
    }
    _srvUser = res.user;
    _srvAuthLost = false;
    _srvHideLogin();
    _srvPaintUserChips();
    const waiters = _srvLoginWaiters; _srvLoginWaiters = [];
    waiters.forEach(fn => fn(res.user));
  } catch (e) {
    msg.textContent = e.status === 401 ? SRV_LOGIN_ERROR
      : e.status === 429 ? 'Too many failed attempts. Wait a few minutes and try again.'
      : e.code === 'no_users' ? 'No accounts exist yet. Ask the person running the server to create one.'
      : (e.message || 'Sign-in failed.');
  } finally { btn.disabled = false; }
  return false;
}
async function srvLogout() {
  if (_workInProgressBlocksAdoption()
      && !confirm('You have unsaved changes. Sign out and DISCARD them?')) return;
  try { await _api('POST', '/api/logout'); } catch (e) { /* signed out either way */ }
  isDirty = false;   /* the user confirmed the discard; do not ask a second time on reload */
  location.reload();
}
function _srvPaintUserChips() {
  document.querySelectorAll('.user-chip').forEach(b => {
    b.hidden = !_srvUser;
    const n = b.querySelector('.user-chip-name');
    if (n && _srvUser) n.textContent = _srvUser.displayName || _srvUser.username;
  });
}
function toggleUserMenu(ev, btn) {
  if (ev) ev.stopPropagation();
  const menu = document.getElementById('userMenu');
  if (!menu) return;
  if (!menu.hidden) { menu.hidden = true; return; }
  const r = btn.getBoundingClientRect();
  menu.style.top = (r.bottom + 6) + 'px';
  menu.style.right = Math.max(8, window.innerWidth - r.right) + 'px';
  document.getElementById('userMenuAdmin').hidden = !(_srvUser && _srvUser.role === 'admin');
  document.getElementById('userMenuWho').textContent = _srvUser
    ? (_srvUser.displayName || _srvUser.username) + ' · ' + _srvUser.username : '';
  menu.hidden = false;
  setTimeout(() => document.addEventListener('click', function close(e) {
    if (!menu.contains(e.target)) { menu.hidden = true; document.removeEventListener('click', close); }
  }), 0);
}
function openChangePassword() {
  document.getElementById('userMenu').hidden = true;
  ['pwCurrent', 'pwNext', 'pwNext2'].forEach(id => { document.getElementById(id).value = ''; });
  document.getElementById('pwMsg').textContent = '';
  document.getElementById('passwordOverlay').classList.add('open');
  setTimeout(() => document.getElementById('pwCurrent').focus(), 0);
}
function closeChangePassword() { document.getElementById('passwordOverlay').classList.remove('open'); }
function handlePasswordOverlayClick(e) { if (e.target === e.currentTarget) closeChangePassword(); }
async function submitChangePassword(ev) {
  if (ev) ev.preventDefault();
  const cur = document.getElementById('pwCurrent').value, next = document.getElementById('pwNext').value;
  const msg = document.getElementById('pwMsg');
  if (next.length < 8) { msg.textContent = 'The new password needs at least 8 characters.'; return false; }
  if (next !== document.getElementById('pwNext2').value) { msg.textContent = 'The two new passwords do not match.'; return false; }
  try {
    await _api('POST', '/api/me/password', { current: cur, next: next });
    closeChangePassword();
    showToast('Password changed.', { type: 'success' });
  } catch (e) { msg.textContent = e.status === 401 || e.status === 403 ? 'The current password is not right.' : e.message; }
  return false;
}

/* ── user administration (admins) ─────────────────────────────────────────────────────────── */
async function openUserAdmin() {
  document.getElementById('userMenu').hidden = true;
  document.getElementById('usersOverlay').classList.add('open');
  document.getElementById('usersMsg').textContent = '';
  await _srvRenderUsers();
}
function closeUserAdmin() { document.getElementById('usersOverlay').classList.remove('open'); }
function handleUsersOverlayClick(e) { if (e.target === e.currentTarget) closeUserAdmin(); }
async function _srvLoadUsers() {
  try { _srvUsersCache = (await _api('GET', '/api/admin/users')).users || []; }
  catch (e) { if (e.status !== 403) throw e; }
  return _srvUsersCache || [];
}
async function _srvRenderUsers() {
  const host = document.getElementById('usersList');
  let users;
  try { users = await _srvLoadUsers(); }
  catch (e) { host.innerHTML = `<div class="confirm-msg">${_escapeHtml(e.message)}</div>`; return; }
  host.innerHTML = users.map(u => {
    const me = _srvUser && u.username === _srvUser.username;
    const un = _escapeHtml(u.username);
    return `<div class="ua-row${u.disabled ? ' ua-disabled' : ''}">`
      + `<span class="ua-name"><strong>${_escapeHtml(u.displayName || u.username)}</strong>`
      + (u.displayName && u.displayName !== u.username ? ` <span class="ua-user">${un}</span>` : '') + `</span>`
      + `<span class="ua-role">${u.role === 'admin' ? 'Admin' : 'User'}${u.disabled ? ' · disabled' : ''}</span>`
      + `<span class="ua-acts">`
      + `<button class="ov-btn" onclick="srvAdminResetPassword('${un}')">Set password</button>`
      + (me ? '' : `<button class="ov-btn" onclick="srvAdminToggleRole('${un}')">${u.role === 'admin' ? 'Make user' : 'Make admin'}</button>`
               + `<button class="ov-btn" onclick="srvAdminToggleDisabled('${un}')">${u.disabled ? 'Enable' : 'Disable'}</button>`)
      + `</span></div>`;
  }).join('') || '<div class="confirm-msg">No users.</div>';
}
async function _srvAdminUpdate(username, body, done) {
  const msg = document.getElementById('usersMsg');
  msg.textContent = '';
  try { await _api('POST', '/api/admin/users/' + encodeURIComponent(username), body); if (done) msg.textContent = done; }
  catch (e) { msg.textContent = e.message; }
  await _srvRenderUsers();
}
function srvAdminResetPassword(username) {
  const pw = prompt('New password for ' + username + ' (at least 8 characters):');
  if (pw === null) return;
  if (pw.length < 8) { document.getElementById('usersMsg').textContent = 'Passwords need at least 8 characters.'; return; }
  _srvAdminUpdate(username, { password: pw, disabled: false }, 'Password set for ' + username + '. The account is enabled.');
}
function srvAdminToggleDisabled(username) {
  const u = (_srvUsersCache || []).find(x => x.username === username);
  if (!u) return;
  _srvAdminUpdate(username, { disabled: !u.disabled });
}
function srvAdminToggleRole(username) {
  const u = (_srvUsersCache || []).find(x => x.username === username);
  if (!u) return;
  _srvAdminUpdate(username, { role: u.role === 'admin' ? 'user' : 'admin' });
}
async function submitNewUser(ev) {
  if (ev) ev.preventDefault();
  const msg = document.getElementById('usersMsg');
  const username = document.getElementById('nuUser').value.trim().toLowerCase();
  const displayName = document.getElementById('nuName').value.trim();
  const password = document.getElementById('nuPass').value;
  const role = document.getElementById('nuAdmin').checked ? 'admin' : 'user';
  if (!/^[a-z0-9_-]{1,40}$/.test(username)) { msg.textContent = 'Usernames use a–z, 0–9, _ and - only (up to 40).'; return false; }
  if (password.length < 8) { msg.textContent = 'Passwords need at least 8 characters.'; return false; }
  try {
    await _api('POST', '/api/admin/users', { username: username, displayName: displayName || username, password: password, role: role });
    ['nuUser', 'nuName', 'nuPass'].forEach(id => { document.getElementById(id).value = ''; });
    document.getElementById('nuAdmin').checked = false;
    msg.textContent = 'Created ' + username + '.';
  } catch (e) { msg.textContent = e.message; }
  await _srvRenderUsers();
  return false;
}
